/* @flow */
import _ from 'lodash';
import Avatar from 'components/Avatar.jsx';
import ChatMessage from 'entities/ChatMessage';
import ChatMessageStore from 'chat/stores/ChatMessageStore';
import cx from 'classnames';
import { Container } from 'flux/utils';
import CurrentUser from 'auth/CurrentUser';
import DeliveryStore from 'stores/DeliveryStore';
import DeliverySummaryStore from 'stores/DeliverySummaryStore';
import DeliveryModal from 'chat/components/DeliveryModal.jsx';
import { subgroupName, EVERYONE } from 'utils/roleHelpers';
import FeatureStore from 'stores/FeatureStore';
import Group from 'entities/Group';
import GroupAvatar from 'groups/components/GroupAvatar.jsx';
import GroupStore from 'groups/stores/GroupStore';
import Link from 'components/Link.jsx';
import Message from 'entities/Message';
import MessageActions from 'groups/actions/MessageActions';
import MessageDelivery from 'entities/MessageDelivery';
import MessageDeliverySummary from 'entities/MessageDeliverySummary';
import MessageItemContent from './MessageItemContent.jsx';
import { mountModal } from 'lib/modal.js';
import Text from 'components/Text.jsx';
import Time from 'components/Time.jsx';
import React from 'react';
import TooltippedReactionButton from 'chat/components/TooltippedReactionButton.jsx';
import ReactionDisplay from 'chat/components/ReactionDisplay.jsx';
import ReactionSummary from 'entities/ReactionSummary';

import type { MessageType } from 'chat/lib/MessageInterfacePropType';

import './styles/AnnouncementMessageItem.scss';


function getDeliverySummary(message) {
  const announcement = message instanceof Message ? message : message.getMessage();
  return DeliverySummaryStore.getByMessageId(announcement.getUuid());
}

type Props = {
  // Can be either a chat message (ChatMessage) or an announcement (Message)
  // In the case that this is a ChatMessage, the associated announcement is
  // accessed with message.getMessage()
  message: MessageType,
  group: Group,
  sentBySelf: boolean,
  senderColor: string,
  senderInitials: string,
  lastOfGroup: boolean,
};

type State = {
  reactionPickerVisible: boolean,
  readReceipt?: MessageDelivery,
  deliverySummary?: MessageDeliverySummary,
  hovered: boolean,
  reactionSummary: ?ReactionSummary,
};

class AnnouncementMessageItem extends React.Component {
  props: Props;

  state: State = {
    reactionSummary: null,
    readReceipt: DeliveryStore.getDeliveryByMessageId(this.props.message.getUuid()),
    deliverySummary: getDeliverySummary(this.props.message),
    reactionPickerVisible: false,
    hovered: false,
  };

  static getStores() {
    return [DeliveryStore, DeliverySummaryStore, ChatMessageStore];
  }

  static calculateState(prevState: ?State, props: Props): State {
    const { message } = props;

    let reactionSummary;
    if (message instanceof ChatMessage) {
      const chatMessages = ChatMessageStore.getChatMessages(props.message.getChatId());
      const chatMessage = chatMessages.find(m => m.id === props.message.id);
      reactionSummary = chatMessage.getReactionSummary();
    }

    return {
      reactionPickerVisible: false,
      hovered: false,
      readReceipt: DeliveryStore.getDeliveryByMessageId(message.getUuid()),
      deliverySummary: getDeliverySummary(message),
      reactionSummary,
    };
  }

  componentWillMount() {
    MessageActions.loadDeliverySummary(this.getAnnouncement());
  }

  getAnnouncement() {
    const { message } = this.props;
    return message instanceof Message ? message : message.getMessage();
  }

  isAnnouncementToOwnedGroup(message) {
    const threadGroupIds = message.getThreads().map(thread => thread.group_id) || [];
    const messageGroupIds = message.getGroupIds() || [];
    return threadGroupIds.concat(messageGroupIds)
        .map(id => GroupStore.getGroup(id))
        .map(group => group && group.isOwnedByUser(CurrentUser.getUser().id))
        .includes(true);
  }

  memberCountForRoles(messageRoles: Array<string>): number {
    const groupRoles = this.props.group.getRoles();
    // Add the number of people in group for each role message was sent to
    return _.sumBy(groupRoles, r => {
      return messageRoles.includes(r.role) ? r.count : 0;
    });
  }

  onHoverReactionPickerShow = () => {
    this.setState({ reactionPickerVisible: true });
  };

  onHoverReactionPickerHide = () => {
    this.setState({ reactionPickerVisible: false });
  };

  onEnterHover = () => {
    this.setState({ hovered: true });
  };

  onLeaveHover = () => {
    this.setState({ hovered: false });
  };

  renderDeliveryInfo() {
    const { group } = this.props;
    const { deliverySummary } = this.state;
    const openReadReceiptModal = () => {
      mountModal(<DeliveryModal summary={deliverySummary} loaded tooSoon />, this);
    };

    let sentCount;
    let readCount;

    if (deliverySummary) {
      sentCount = deliverySummary.getTotalSent();
      readCount = deliverySummary.getReadCount();
    } else {
      const roles = this.getAnnouncement().getRoles();
      const defaultRecipients = roles.includes(EVERYONE) ?
        group.getMembershipsCount() :
        this.memberCountForRoles(roles);
      sentCount = defaultRecipients;
      readCount = 0;
    }

    return (
      <div className="AnnouncementMessageItem-head-deliveryInfo">
        <span> Sent to {sentCount} </span>
        <span> {' • '} </span>
        <Link
          onClick={openReadReceiptModal}>
          Seen by {readCount}
        </Link>
      </div>
    );
  }

  renderAnnouncementHeader() {
    const { group } = this.props;
    const isAnnouncementToOwnedGroup = this.isAnnouncementToOwnedGroup(this.getAnnouncement());

    const groupName = group.getName();
    const roles = this.getAnnouncement().getRoles();
    const groupHeading = roles.includes(EVERYONE) ?
      groupName :
      `${groupName} ${roles.map(role => subgroupName[role]).join(' & ')}`;

    return (
      <div className="AnnouncementMessageItem-head">
        <GroupAvatar
          className="AnnouncementMessageItem-announcementBadge"
          group={group}
        />
        <div className="AnnouncementMessageItem-head-classInfo">
          <Text className="AnnouncementMessageItem-groupName" weight="semibold" truncate>{groupHeading}</Text>
          {isAnnouncementToOwnedGroup && this.renderDeliveryInfo()}
        </div>
      </div>
    );
  }

  renderReactions() {
    const { message, sentBySelf } = this.props;
    return (
      <ReactionDisplay
        message={message}
        reactionSummary={this.state.reactionSummary}
        sentBySelf={sentBySelf}
      />
    );
  }

  renderHover() {
    const { message } = this.props;
    const reactionsEnabled = FeatureStore.hasFeature('web.reactions') && message instanceof ChatMessage;

    return (
      <div className="AnnouncementMessageItem-hover">
        <div className="MessageItem-hover-receipt">
          <Time
            date={message.getSentAt()}
            hour="numeric"
            minute="2-digit"
          />
        </div>
        {reactionsEnabled && !message.hasReactions() &&
          <TooltippedReactionButton
            message={message}
            onShow={this.onHoverReactionPickerShow}
            onHide={this.onHoverReactionPickerHide}
            reactionPickerVisible={this.state.reactionPickerVisible}
          />
        }
      </div>
    );
  }

  renderDroplet() {
    const { senderColor, senderInitials, lastOfGroup, sentBySelf } = this.props;
    return (lastOfGroup && !sentBySelf) ?
      (
        <span className="AnnouncementMessageItem-droplet">
          <Avatar
            colorHex={senderColor}
            size="small"
            text={senderInitials}
          />
        </span>
      ) : null;
  }

  render() {
    const { sentBySelf, message } = this.props;
    const { hovered, reactionPickerVisible } = this.state;
    const reactionsEnabled = FeatureStore.hasFeature('web.reactions') && message instanceof ChatMessage;

    return (
      <div
        className={cx('AnnouncementMessageItem', {
          'AnnouncementMessageItem-self': sentBySelf,
          'AnnouncementMessageItem-other': !sentBySelf,
          'AnnouncementMessageItem-hovered': hovered || reactionPickerVisible,
        })}>
        <div
          className="AnnouncementMessageItem-row"
          onMouseEnter={this.onEnterHover}
          onMouseLeave={this.onLeaveHover}>
          {this.renderHover()}
          <div className="AnnouncementMessageItem-container">
            {this.renderAnnouncementHeader()}
            <MessageItemContent
              className="AnnouncementMessageItem-content"
              sentBySelf={sentBySelf}
              message={message}
              failed={false}
            />
          </div>
          {this.renderDroplet()}
        </div>
        {reactionsEnabled && this.renderReactions()}
      </div>
    );
  }
}

export default Container.create(AnnouncementMessageItem, { withProps: true });



// WEBPACK FOOTER //
// ./src/chat/components/AnnouncementMessageItem.jsx